/**
 * Community Management Dialogs for Master Database
 * - Tag Manager Dialog
 * - Bulk Achievement Tags Dialog
 * - Pending Challenges Dialog
 * - Pending Organizers Dialog
 * - Manage Content Dialog
 */
import { Loader2, Plus, Check, X, Edit2, Trash2, Tag, Target, Layers, Lightbulb, Settings, ListTodo, ClipboardList, Grid3X3, MapPin } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";
import { buildTagSections } from "./groupedTags";

const API = `${getApiUrl()}/api`;

/**
 * Tag Manager Dialog - Create and manage unified game tags
 */
export function TagManagerDialog({
  open,
  onOpenChange,
  unifiedTags,
  newTagName,
  onNewTagNameChange,
  newTagColor,
  onNewTagColorChange,
  newTagDescription,
  onNewTagDescriptionChange,
  onCreateTag,
  editingTag,
  onEditingTagChange,
  onUpdateTag,
  onDeleteTag,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-teal-400" />
            Manage Unified Game Tags
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Create and manage game content tags that can be applied across all platforms and systems
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 pt-4">
          {/* Create New Tag */}
          <div className="bg-[#121212] border border-white/10 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-medium text-white/80">Create New Tag</h3>
            <div className="grid grid-cols-3 gap-3">
              <Input
                placeholder="Tag name..."
                value={newTagName}
                onChange={(e) => onNewTagNameChange(e.target.value)}
                className="bg-[#0A0A0A] border-white/10"
                data-testid="new-tag-name-input"
              />
              <Input
                type="color"
                value={newTagColor}
                onChange={(e) => onNewTagColorChange(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 h-10 w-full cursor-pointer"
                title="Tag color"
              />
              <Button 
                onClick={onCreateTag}
                className="bg-teal-600 hover:bg-teal-500"
                data-testid="create-tag-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Tag
              </Button>
            </div>
            <Input
              placeholder="Description (optional)..."
              value={newTagDescription}
              onChange={(e) => onNewTagDescriptionChange(e.target.value)}
              className="bg-[#0A0A0A] border-white/10"
            />
          </div>
          
          {/* Existing Tags List */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-white/80">
              Existing Tags ({unifiedTags.length})
            </h3>
            
            {unifiedTags.length === 0 ? (
              <p className="text-white/40 text-sm italic py-4 text-center">
                No unified tags yet. Create one above!
              </p>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {unifiedTags.map(tag => (
                  <div 
                    key={tag.id}
                    className="bg-[#121212] border border-white/10 rounded-lg p-3 flex items-center justify-between group"
                  >
                    {editingTag?.id === tag.id ? (
                      <div className="flex-1 flex items-center gap-3">
                        <Input
                          value={editingTag.name}
                          onChange={(e) => onEditingTagChange({...editingTag, name: e.target.value})}
                          className="bg-[#0A0A0A] border-white/10 flex-1"
                        />
                        <Input
                          type="color"
                          value={editingTag.color}
                          onChange={(e) => onEditingTagChange({...editingTag, color: e.target.value})}
                          className="bg-[#0A0A0A] border-white/10 h-10 w-16 cursor-pointer"
                        />
                        <Input
                          placeholder="Description..."
                          value={editingTag.description || ""}
                          onChange={(e) => onEditingTagChange({...editingTag, description: e.target.value})}
                          className="bg-[#0A0A0A] border-white/10 flex-1"
                        />
                        <Button 
                          size="sm" 
                          onClick={onUpdateTag}
                          className="bg-green-600 hover:bg-green-500"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => onEditingTagChange(null)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-4 h-4 rounded-full" 
                            style={{ backgroundColor: tag.color }}
                          />
                          <span className="font-medium">{tag.name}</span>
                          {tag.description && (
                            <span className="text-white/40 text-sm">— {tag.description}</span>
                          )}
                          {tag.isDefault && (
                            <span className="text-xs bg-white/10 px-2 py-0.5 rounded">Default</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => onEditingTagChange({...tag})}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="text-red-400 hover:text-red-300"
                            onClick={() => onDeleteTag(tag.id, tag.name)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Per-Achievement Tag Editor Dialog
 * Shows each selected achievement with individual tag checkboxes.
 * Achievements sorted: base game first, then DLC groups, alphabetical within each.
 */
export function BulkAchievementTagsDialog({
  open,
  onOpenChange,
  selectedAchievements,
  predefinedTags,
  onApply,
  tagGroups = [],
}) {
  const [tagEdits, setTagEdits] = useState({});
  // Per-achievement secret-flag staging (mirrors `tagEdits` shape: { [achId]: bool }).
  // Applied together with the tag updates so admins can flip Secret without
  // a separate per-achievement edit.
  const [secretEdits, setSecretEdits] = useState({});
  // Per-achievement Per-Character flag staging — same pattern as `secretEdits`.
  const [perCharEdits, setPerCharEdits] = useState({});
  const [saving, setSaving] = useState(false);

  // Initialize tag edits from achievement data when dialog opens
  useEffect(() => {
    if (open && selectedAchievements?.length) {
      const initial = {};
      const initialSecret = {};
      const initialPerChar = {};
      selectedAchievements.forEach(ach => {
        initial[ach.id] = new Set(ach.tags || []);
        initialSecret[ach.id] = !!ach.isSecret;
        initialPerChar[ach.id] = !!ach.isPerCharacter;
      });
      setTagEdits(initial);
      setSecretEdits(initialSecret);
      setPerCharEdits(initialPerChar);
    }
  }, [open, selectedAchievements]);

  // Sort: base game first, then DLC groups, alpha within each
  const sortedAchievements = useMemo(() => {
    if (!selectedAchievements) return [];
    return [...selectedAchievements].sort((a, b) => {
      const aIsDlc = a.dlcId || a.dlcName ? 1 : 0;
      const bIsDlc = b.dlcId || b.dlcName ? 1 : 0;
      if (aIsDlc !== bIsDlc) return aIsDlc - bIsDlc;
      const dlcCmp = (a.dlcName || "").toLowerCase().localeCompare((b.dlcName || "").toLowerCase());
      if (dlcCmp !== 0) return dlcCmp;
      return (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase());
    });
  }, [selectedAchievements]);

  // Group by DLC for section headers
  const groups = useMemo(() => {
    const result = [];
    let currentGroup = null;
    sortedAchievements.forEach(ach => {
      const groupKey = ach.dlcName || "__base__";
      if (groupKey !== currentGroup) {
        currentGroup = groupKey;
        result.push({ type: "header", label: groupKey === "__base__" ? "Base Game" : ach.dlcName });
      }
      result.push({ type: "achievement", data: ach });
    });
    return result;
  }, [sortedAchievements]);

  const toggleTag = (achId, tagId) => {
    setTagEdits(prev => {
      const next = { ...prev };
      const tags = new Set(next[achId] || []);
      if (tags.has(tagId)) tags.delete(tagId);
      else tags.add(tagId);
      next[achId] = tags;
      return next;
    });
  };

  // Toggle a tag for ALL achievements at once
  const toggleTagForAll = (tagId) => {
    setTagEdits(prev => {
      const achIds = sortedAchievements.map(a => a.id);
      const allHave = achIds.every(id => (prev[id] || new Set()).has(tagId));
      const next = { ...prev };
      achIds.forEach(id => {
        const tags = new Set(next[id] || []);
        if (allHave) tags.delete(tagId);
        else tags.add(tagId);
        next[id] = tags;
      });
      return next;
    });
  };

  // Bulk-apply Secret / Per Character across every selected achievement.
  // Same tri-state UX as tags: click flips ALL selected achievements on
  // (unless every one already has it, in which case it flips them all off).
  const toggleFlagForAll = (flag) => {
    const setter = flag === "secret" ? setSecretEdits : setPerCharEdits;
    setter(prev => {
      const achIds = sortedAchievements.map(a => a.id);
      const allHave = achIds.every(id => !!prev[id]);
      const next = { ...prev };
      achIds.forEach(id => { next[id] = !allHave; });
      return next;
    });
  };

  const flagAllStatus = useMemo(() => {
    const achIds = sortedAchievements.map(a => a.id);
    const secretCount = achIds.filter(id => !!secretEdits[id]).length;
    const perCharCount = achIds.filter(id => !!perCharEdits[id]).length;
    const stateFor = (count) => count === 0 ? "none" : count === achIds.length ? "all" : "partial";
    return {
      secret: { count: secretCount, state: stateFor(secretCount) },
      perChar: { count: perCharCount, state: stateFor(perCharCount) },
    };
  }, [sortedAchievements, secretEdits, perCharEdits]);

  // For each tag, compute how many achievements currently have it (for the "Apply to All" bar)
  const tagAllStatus = useMemo(() => {
    const status = {};
    const achIds = sortedAchievements.map(a => a.id);
    (predefinedTags || []).filter(t => !t.isGameTag).forEach(tag => {
      const count = achIds.filter(id => (tagEdits[id] || new Set()).has(tag.id)).length;
      status[tag.id] = {
        count,
        state: count === 0 ? "none" : count === achIds.length ? "all" : "partial",
      };
    });
    return status;
  }, [tagEdits, sortedAchievements, predefinedTags]);

  // Count how many achievements were changed
  const changedCount = useMemo(() => {
    if (!selectedAchievements) return 0;
    let count = 0;
    selectedAchievements.forEach(ach => {
      const original = new Set(ach.tags || []);
      const edited = tagEdits[ach.id] || new Set();
      const tagsChanged = original.size !== edited.size || [...original].some(t => !edited.has(t));
      const secretChanged = !!ach.isSecret !== !!secretEdits[ach.id];
      const perCharChanged = !!ach.isPerCharacter !== !!perCharEdits[ach.id];
      if (tagsChanged || secretChanged || perCharChanged) {
        count++;
      }
    });
    return count;
  }, [selectedAchievements, tagEdits, secretEdits, perCharEdits]);

  const handleApply = async () => {
    setSaving(true);
    // Build updates only for changed achievements (tags OR secret OR per-char changed)
    const updates = [];
    selectedAchievements.forEach(ach => {
      const original = new Set(ach.tags || []);
      const edited = tagEdits[ach.id] || new Set();
      const tagsChanged = original.size !== edited.size || [...original].some(t => !edited.has(t));
      const editedSecret = !!secretEdits[ach.id];
      const secretChanged = !!ach.isSecret !== editedSecret;
      const editedPerChar = !!perCharEdits[ach.id];
      const perCharChanged = !!ach.isPerCharacter !== editedPerChar;
      if (tagsChanged || secretChanged || perCharChanged) {
        const update = { achievement_id: ach.id, tags: Array.from(edited) };
        // Always include current value when modified so the backend can
        // both turn the flag on AND off.
        if (secretChanged) update.isSecret = editedSecret;
        if (perCharChanged) update.isPerCharacter = editedPerChar;
        updates.push(update);
      }
    });
    await onApply(updates);
    setSaving(false);
  };

  const achievementTags = (predefinedTags || []).filter(t => !t.isGameTag);
  // Pre-compute the grouped sections so we don't rebuild them per achievement row.
  const tagSections = buildTagSections(achievementTags, tagGroups);
  const showGroupHeaders = tagGroups.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white w-[95vw] max-w-[1400px] max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-3 flex-shrink-0 border-b border-white/10">
          <DialogTitle className="flex items-center gap-2" data-testid="per-ach-tag-editor-title">
            <Tag className="w-5 h-5 text-teal-400" />
            Tag Achievements
          </DialogTitle>
          <DialogDescription className="text-white/60">
            {selectedAchievements?.length || 0} achievement{(selectedAchievements?.length || 0) !== 1 ? 's' : ''} selected
            {changedCount > 0 && <span className="text-amber-400 ml-2">({changedCount} modified)</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-1" data-testid="per-ach-tag-list">
          {/* Apply to All section */}
          <div className="pb-3 mb-2 border-b border-white/10" data-testid="apply-to-all-section">
            <div className="text-[11px] font-semibold text-white/50 uppercase tracking-wider mb-2">Apply to All</div>
            {/* Bulk flag toggles — Secret & Per Character. Tri-state: none /
                partial / all mirror the tag chips below. */}
            <div className="flex flex-wrap gap-2 mb-3" data-testid="apply-all-flags">
              {[
                { key: "secret", label: "Secret", color: "#f59e0b", testid: "apply-all-secret" },
                { key: "perChar", label: "Per Character", color: "#e11d74", testid: "apply-all-per-character" },
              ].map(({ key, label, color, testid }) => {
                const info = flagAllStatus[key];
                const status = info.state;
                const count = info.count;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleFlagForAll(key)}
                    data-testid={testid}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-all border ${
                      status === "all"
                        ? "ring-1 ring-offset-1 ring-offset-[#0A0A0A]"
                        : status === "partial"
                        ? "ring-1 ring-offset-1 ring-offset-[#0A0A0A] ring-dashed"
                        : "hover:bg-white/10"
                    }`}
                    style={status === "all" ? {
                      backgroundColor: color + "30",
                      color,
                      borderColor: color + "60",
                    } : status === "partial" ? {
                      backgroundColor: color + "15",
                      color: color + "CC",
                      borderColor: color + "40",
                      borderStyle: "dashed",
                    } : {
                      backgroundColor: "rgba(255,255,255,0.06)",
                      color: "rgba(255,255,255,0.6)",
                      borderColor: "rgba(255,255,255,0.1)",
                    }}
                    title={
                      status === "all"
                        ? `Click to remove ${label} from all selected`
                        : `Click to apply ${label} to all selected`
                    }
                  >
                    <span
                      aria-hidden="true"
                      className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm border ${
                        status === "all" ? "" : "bg-transparent"
                      }`}
                      style={{
                        borderColor: status === "none" ? "rgba(255,255,255,0.35)" : color,
                        backgroundColor: status === "all" ? color : "transparent",
                      }}
                    >
                      {status === "all" && (
                        <svg viewBox="0 0 12 12" className="w-3 h-3" fill="none" stroke="#0A0A0A" strokeWidth="2.5">
                          <path d="M2 6 L5 9 L10 3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                      {status === "partial" && (
                        <span className="w-2 h-0.5" style={{ backgroundColor: color }} />
                      )}
                    </span>
                    {label}
                    {count > 0 && (
                      <span className="text-[10px] font-semibold opacity-80" data-testid={`${testid}-count`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="space-y-2">
              {tagSections.map(section => (
                <div key={section.id} data-testid={`apply-all-group-${section.id}`}>
                  {showGroupHeaders && (
                    <p className="text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-1">
                      {section.name}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {section.tags.map(tag => {
                      const info = tagAllStatus[tag.id] || { state: "none", count: 0 };
                      const status = info.state;
                      const count = info.count;
                      return (
                        <button
                          key={tag.id}
                          onClick={() => toggleTagForAll(tag.id)}
                          data-testid={`apply-all-tag-${tag.id}`}
                          className={`px-2.5 py-1 rounded text-[11px] font-medium transition-all border relative ${
                            status === "all"
                              ? "ring-1 ring-offset-1 ring-offset-[#0A0A0A]"
                              : status === "partial"
                              ? "ring-1 ring-offset-1 ring-offset-[#0A0A0A] ring-dashed"
                              : "hover:bg-white/10"
                          }`}
                          style={status === "all" ? {
                            backgroundColor: tag.color + "30",
                            color: tag.color,
                            borderColor: tag.color + "60",
                          } : status === "partial" ? {
                            backgroundColor: tag.color + "15",
                            color: tag.color + "AA",
                            borderColor: tag.color + "40",
                            borderStyle: "dashed",
                          } : {
                            backgroundColor: "rgba(255,255,255,0.06)",
                            color: "rgba(255,255,255,0.5)",
                            borderColor: "rgba(255,255,255,0.1)",
                          }}
                        >
                          {tag.name}
                          {count > 0 && (
                            <span
                              className="ml-1.5 text-[9px] font-semibold opacity-80"
                              data-testid={`apply-all-tag-${tag.id}-count`}
                            >
                              {count}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {groups.map((item, idx) => {
            if (item.type === "header") {
              return (
                <div key={`hdr-${idx}`} className="sticky top-0 bg-[#0A0A0A] z-10 py-2 border-b border-white/5">
                  <span className="text-xs font-semibold text-white/40 uppercase tracking-wider">
                    {item.label}
                  </span>
                </div>
              );
            }
            const ach = item.data;
            const achTags = tagEdits[ach.id] || new Set();
            return (
              <div key={ach.id} className="py-3 border-b border-white/5" data-testid={`ach-tag-row-${ach.id}`}>
                <div className="flex items-start gap-3 mb-2">
                  {ach.imageUrl && (
                    <img
                      src={ach.imageUrl}
                      alt={ach.name}
                      className="w-10 h-10 rounded object-contain bg-black/50 flex-shrink-0 mt-0.5"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-white/90 truncate">{ach.name}</span>
                      {ach.score > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 flex-shrink-0">
                          {ach.score}G
                        </span>
                      )}
                      {ach.trophyType && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400 capitalize flex-shrink-0">
                          {ach.trophyType}
                        </span>
                      )}
                    </div>
                    {ach.description && (
                      <p className="text-[11px] text-white/70 mt-0.5 line-clamp-2">{ach.description}</p>
                    )}
                  </div>
                  {/* Per-row "Secret" toggle — staged like a tag edit, sent
                      with the same Apply button. Sits to the right of the
                      name + description (per the original feature spec). */}
                  <label
                    className="flex items-center gap-1.5 flex-shrink-0 text-[11px] text-white/60 cursor-pointer select-none px-2 py-1 rounded border border-amber-500/20 hover:border-amber-500/40 transition-colors"
                    title="Hide name, description, and/or image until the user unlocks this achievement"
                    data-testid={`ach-secret-toggle-${ach.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!secretEdits[ach.id]}
                      onChange={(e) =>
                        setSecretEdits((prev) => ({ ...prev, [ach.id]: e.target.checked }))
                      }
                      className="w-3.5 h-3.5 accent-amber-400"
                    />
                    <span className={secretEdits[ach.id] ? "text-amber-300" : ""}>Secret</span>
                  </label>
                  {/* Per-row Per-Character toggle — mirrors Secret, staged in
                      `perCharEdits` and sent alongside tags on Apply. */}
                  <label
                    className="flex items-center gap-1.5 flex-shrink-0 text-[11px] text-white/60 cursor-pointer select-none px-2 py-1 rounded border transition-colors"
                    style={{ borderColor: "rgba(225,29,116,0.2)" }}
                    title="Users can log per-character unlocks on this achievement"
                    data-testid={`ach-per-character-toggle-${ach.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!perCharEdits[ach.id]}
                      onChange={(e) =>
                        setPerCharEdits((prev) => ({ ...prev, [ach.id]: e.target.checked }))
                      }
                      className="w-3.5 h-3.5"
                      style={{ accentColor: "#e11d74" }}
                    />
                    <span style={{ color: perCharEdits[ach.id] ? "#f472b6" : undefined }}>
                      Per Char
                    </span>
                  </label>
                </div>
                <div className="space-y-1.5">
                  {tagSections.map(section => (
                    <div key={section.id}>
                      {showGroupHeaders && (
                        <p className="text-[9px] font-semibold text-white/30 uppercase tracking-wider mb-0.5">
                          {section.name}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-1.5">
                        {section.tags.map(tag => {
                          const isActive = achTags.has(tag.id);
                          return (
                            <button
                              key={tag.id}
                              onClick={() => toggleTag(ach.id, tag.id)}
                              data-testid={`tag-btn-${ach.id}-${tag.id}`}
                              className={`px-2 py-0.5 rounded text-[11px] font-medium transition-all border ${
                                isActive
                                  ? "ring-1 ring-offset-1 ring-offset-[#0A0A0A]"
                                  : "hover:bg-white/10"
                              }`}
                              style={isActive ? {
                                backgroundColor: tag.color + "30",
                                color: tag.color,
                                borderColor: tag.color + "60",
                              } : {
                                backgroundColor: "rgba(255,255,255,0.06)",
                                color: "rgba(255,255,255,0.5)",
                                borderColor: "rgba(255,255,255,0.1)",
                              }}
                            >
                              {tag.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-between items-center gap-3 px-6 py-4 border-t border-white/10 flex-shrink-0">
          <span className="text-xs text-white/40">
            {changedCount === 0 ? "No changes" : `${changedCount} achievement${changedCount !== 1 ? 's' : ''} modified`}
          </span>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/20">
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              className="bg-teal-600 hover:bg-teal-500"
              disabled={changedCount === 0 || saving}
              data-testid="apply-per-ach-tags-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Apply ({changedCount})
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Pending Challenges Dialog
 */
export function PendingChallengesDialog({
  open,
  onOpenChange,
  pendingChallenges,
  onApprove,
  onReject,
  onEdit,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-400">
            <Target className="w-5 h-5" />
            Pending Challenge Submissions ({pendingChallenges?.length || 0})
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Review and approve/reject community challenge submissions
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto">
          {!pendingChallenges || pendingChallenges.length === 0 ? (
            <div className="text-center py-12 text-white/50">
              <Target className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No pending challenge submissions</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingChallenges?.map((challenge) => (
                <div key={challenge.id} className="p-4 rounded-lg border border-orange-500/20 bg-orange-500/5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h4 className="font-medium text-white">{challenge.name}</h4>
                        {challenge.difficulty && (
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            challenge.difficulty === 'easy' ? 'bg-green-500/20 text-green-400' :
                            challenge.difficulty === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                            challenge.difficulty === 'hard' ? 'bg-orange-500/20 text-orange-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {challenge.difficulty}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-white/60 mb-2">{challenge.description || "No description"}</p>
                      <div className="flex flex-wrap gap-2 text-xs text-white/40">
                        <span className="bg-white/10 px-2 py-1 rounded">{challenge.gameName}</span>
                        <span className="bg-white/5 px-2 py-1 rounded">{challenge.gamePlatform}</span>
                        {challenge.timerSeconds && (
                          <span className="bg-white/5 px-2 py-1 rounded">Timer: {Math.floor(challenge.timerSeconds / 60)}m</span>
                        )}
                        {challenge.prerequisiteAchievementNames?.length > 0 && (
                          <span className="bg-white/5 px-2 py-1 rounded">{challenge.prerequisiteAchievementNames.length} prerequisites</span>
                        )}
                      </div>
                      {challenge.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {challenge.tags.map(tag => (
                            <span key={tag} className="px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-300/80 text-[10px] border border-purple-500/20">{tag}</span>
                          ))}
                        </div>
                      )}
                      <div className="mt-2 text-xs text-white/30">
                        {challenge.submittedByUsername && <span>Submitted by: {challenge.submittedByUsername} • </span>}
                        {(challenge.createdAt || challenge.submittedAt) && <span>{new Date(challenge.createdAt || challenge.submittedAt).toLocaleDateString()}</span>}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onEdit?.(challenge, "challenges")}
                        className="border-white/20 text-white/60 hover:bg-white/5"
                        data-testid={`edit-pending-challenge-${challenge.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => onApprove(challenge.id)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onReject(challenge.id)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Pending Organizers Dialog
 */
export function PendingOrganizersDialog({
  open,
  onOpenChange,
  pendingOrganizers,
  onApprove,
  onReject,
  onEdit,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-cyan-400">
            <Layers className="w-5 h-5" />
            Pending Organizer Submissions ({pendingOrganizers?.length || 0})
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Review and approve/reject community organizer submissions
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto">
          {!pendingOrganizers || pendingOrganizers.length === 0 ? (
            <div className="text-center py-12 text-white/50">
              <Layers className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No pending organizer submissions</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingOrganizers?.map((organizer) => (
                <div key={organizer.id} className="p-4 rounded-lg border border-cyan-500/20 bg-cyan-500/5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="font-medium text-white mb-1">{organizer.name}</h4>
                      <p className="text-sm text-white/60 mb-2">{organizer.description || "No description"}</p>
                      <div className="flex flex-wrap gap-2 text-xs text-white/40 mb-2">
                        <span className="bg-white/10 px-2 py-1 rounded">{organizer.gameName}</span>
                        <span className="bg-white/5 px-2 py-1 rounded">{organizer.gamePlatform}</span>
                        <span className="bg-white/5 px-2 py-1 rounded">{organizer.achievementNames?.length || 0} achievements</span>
                      </div>
                      {organizer.achievementNames?.length > 0 && (
                        <div className="p-2 bg-black/30 rounded text-xs text-white/50">
                          <p className="font-medium mb-1">Achievements included:</p>
                          <p className="line-clamp-3">{organizer.achievementNames.join(", ")}</p>
                        </div>
                      )}
                      {organizer.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {organizer.tags.map(tag => (
                            <span key={tag} className="px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-300/80 text-[10px] border border-purple-500/20">{tag}</span>
                          ))}
                        </div>
                      )}
                      <div className="mt-2 text-xs text-white/30">
                        {organizer.submittedByUsername && <span>Submitted by: {organizer.submittedByUsername} • </span>}
                        {organizer.submittedAt && <span>{new Date(organizer.submittedAt).toLocaleDateString()}</span>}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onEdit?.(organizer, "organizers")}
                        className="border-white/20 text-white/60 hover:bg-white/5"
                        data-testid={`edit-pending-organizer-${organizer.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => onApprove(organizer.id)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onReject(organizer.id)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Pending Game Planners Dialog
 */
export function PendingPlannersDialog({
  open,
  onOpenChange,
  pendingPlanners,
  onApprove,
  onReject,
  onEdit,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-indigo-400">
            <ClipboardList className="w-5 h-5" />
            Pending Planner Submissions ({pendingPlanners?.length || 0})
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Review and approve/reject community game planner submissions
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto">
          {!pendingPlanners || pendingPlanners.length === 0 ? (
            <div className="text-center py-12 text-white/50">
              <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No pending planner submissions</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingPlanners?.map((planner) => (
                <div key={planner.id} className="p-4 rounded-lg border border-indigo-500/20 bg-indigo-500/5" data-testid={`pending-planner-${planner.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="font-medium text-white mb-1">{planner.name}</h4>
                      <p className="text-sm text-white/60 mb-2">{planner.description || "No description"}</p>
                      <div className="flex flex-wrap gap-2 text-xs text-white/40 mb-2">
                        <span className="bg-white/10 px-2 py-1 rounded">{planner.gameName}</span>
                        <span className="bg-white/5 px-2 py-1 rounded">{planner.gamePlatform}</span>
                        <span className="bg-white/5 px-2 py-1 rounded">
                          {planner.organizerData?.length || 0} organizer{(planner.organizerData?.length || 0) !== 1 ? "s" : ""}
                        </span>
                      </div>
                      {planner.organizerData?.map((org, idx) => (
                        <div key={idx} className="p-2 bg-black/30 rounded text-xs text-white/50 mb-1">
                          <p className="font-medium mb-1">{org.name}:</p>
                          <p className="line-clamp-2">{org.achievementNames?.join(", ") || "No achievements"}</p>
                        </div>
                      ))}
                      {planner.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {planner.tags.map(tag => (
                            <span key={tag} className="px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-300/80 text-[10px] border border-purple-500/20">{tag}</span>
                          ))}
                        </div>
                      )}
                      <div className="mt-2 text-xs text-white/30">
                        {planner.submittedByUsername && <span>Submitted by: {planner.submittedByUsername} &bull; </span>}
                        {planner.createdAt && <span>{new Date(planner.createdAt).toLocaleDateString()}</span>}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onEdit?.(planner, "planners")}
                        className="border-white/20 text-white/60 hover:bg-white/5"
                        data-testid={`edit-pending-planner-${planner.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => onApprove(planner.id)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`approve-planner-${planner.id}`}
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onReject(planner.id)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        data-testid={`reject-planner-${planner.id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Pending Shared Checklists Dialog
 */
export function PendingChecklistsDialog({
  open,
  onOpenChange,
  pendingChecklists,
  onApprove,
  onReject,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="pending-checklists-dialog"
        className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-pink-400">
            <ListTodo className="w-5 h-5" />
            Pending Shared Checklists ({pendingChecklists?.length || 0})
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Review and approve/reject community-submitted checklist templates
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {!pendingChecklists || pendingChecklists.length === 0 ? (
            <div className="text-center py-12 text-white/50">
              <ListTodo className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No pending checklist submissions</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingChecklists.map((cl) => (
                <div
                  key={cl.id}
                  className="p-4 rounded-lg border border-pink-500/20 bg-pink-500/5"
                  data-testid={`pending-checklist-${cl.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-white mb-1">{cl.title}</h4>
                      {cl.description && (
                        <p className="text-sm text-white/60 mb-2">{cl.description}</p>
                      )}
                      <div className="flex flex-wrap gap-2 text-xs text-white/40 mb-2">
                        {cl.gameName && (
                          <span className="bg-white/10 px-2 py-1 rounded">{cl.gameName}</span>
                        )}
                        {cl.category && (
                          <span className="bg-white/5 px-2 py-1 rounded">{cl.category}</span>
                        )}
                        <span className="bg-white/5 px-2 py-1 rounded">
                          {cl.itemCount ?? cl.items?.length ?? 0} item{(cl.itemCount ?? cl.items?.length ?? 0) !== 1 ? "s" : ""}
                        </span>
                      </div>
                      {cl.items?.length > 0 && (
                        <div className="p-2 bg-black/30 rounded text-xs text-white/60 space-y-0.5 max-h-32 overflow-y-auto">
                          {cl.items.slice(0, 6).map((it, idx) => (
                            <div key={idx} className="truncate">• {it.text}</div>
                          ))}
                          {cl.items.length > 6 && (
                            <div className="text-white/30 italic">…and {cl.items.length - 6} more</div>
                          )}
                        </div>
                      )}
                      <div className="mt-2 text-xs text-white/30">
                        {(cl.submittedByUsername || cl.authorName) && (
                          <span>Submitted by: {cl.submittedByUsername || cl.authorName} &bull; </span>
                        )}
                        {(cl.submittedAt || cl.createdAt) && (
                          <span>{new Date(cl.submittedAt || cl.createdAt).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        onClick={() => onApprove(cl.id)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`approve-checklist-${cl.id}`}
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onReject(cl.id)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        data-testid={`reject-checklist-${cl.id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Manage Community Content Dialog
 */
export function ManageContentDialog({
  open,
  onOpenChange,
  activeTab,
  onTabChange,
  loading,
  approvedChallenges,
  approvedOrganizers,
  approvedFacts,
  approvedChecklists,
  approvedGamePlanners,
  approvedGrids,
  approvedMaps,
  onDeleteChallenge,
  onDeleteOrganizer,
  onDeleteFact,
  onDeleteChecklist,
  onDeletePlanner,
  onDeleteGrid,
  onDeleteMap,
  onUpdateTags,
  onEdit,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-green-400">
            <Settings className="w-5 h-5" />
            Manage Community Content
          </DialogTitle>
          <DialogDescription className="text-white/60">
            View and delete approved challenges, organizers, trivia, and checklists
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={onTabChange} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-7 bg-black/50">
            <TabsTrigger value="challenges" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400 text-xs">
              <Target className="w-3.5 h-3.5 mr-1" />
              <span className="hidden sm:inline">Challenges</span> ({approvedChallenges?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="organizers" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 text-xs">
              <Layers className="w-3.5 h-3.5 mr-1" />
              <span className="hidden sm:inline">Organizers</span> ({approvedOrganizers?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="planners" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400 text-xs">
              <ClipboardList className="w-3.5 h-3.5 mr-1" />
              <span className="hidden sm:inline">Planners</span> ({approvedGamePlanners?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="grids" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400 text-xs">
              <Grid3X3 className="w-3.5 h-3.5 mr-1" />
              <span className="hidden sm:inline">Grids</span> ({approvedGrids?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="maps" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400 text-xs" data-testid="manage-tab-maps">
              <MapPin className="w-3.5 h-3.5 mr-1" />
              <span className="hidden sm:inline">Maps</span> ({approvedMaps?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="checklists" className="data-[state=active]:bg-teal-500/20 data-[state=active]:text-teal-400 text-xs">
              <ListTodo className="w-3.5 h-3.5 mr-1" />
              <span className="hidden sm:inline">Checklists</span> ({approvedChecklists?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="trivia" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400 text-xs">
              <Lightbulb className="w-3.5 h-3.5 mr-1" />
              <span className="hidden sm:inline">Trivia</span> ({approvedFacts?.length || 0})
            </TabsTrigger>
          </TabsList>

          {/* Challenges Tab */}
          <TabsContent value="challenges" className="flex-1 overflow-y-auto mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
              </div>
            ) : !approvedChallenges || approvedChallenges.length === 0 ? (
              <div className="text-center py-12 text-white/50">
                <Target className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No approved challenges</p>
              </div>
            ) : (
              <div className="space-y-2">
                {approvedChallenges.map((challenge) => (
                  <div key={challenge.id} className="p-3 rounded-lg border border-white/10 bg-white/5 flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-white">{challenge.name}</span>
                        <span className="text-xs bg-white/10 px-2 py-0.5 rounded">{challenge.gameName}</span>
                        {challenge.difficulty && (
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            challenge.difficulty === 'easy' ? 'bg-green-500/20 text-green-400' :
                            challenge.difficulty === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>{challenge.difficulty}</span>
                        )}
                      </div>
                      <p className="text-xs text-white/40 mt-1 truncate">{challenge.description || "No description"}</p>
                      <InlineTagEditor
                        tags={challenge.tags || []}
                        contentType="challenges"
                        itemId={challenge.id}
                        onUpdateTags={onUpdateTags}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onEdit?.(challenge, "challenges")}
                        className="border-white/20 text-white/60 hover:bg-white/5"
                        data-testid={`edit-approved-challenge-${challenge.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onDeleteChallenge(challenge.id, challenge.name)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Organizers Tab */}
          <TabsContent value="organizers" className="flex-1 overflow-y-auto mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
              </div>
            ) : !approvedOrganizers || approvedOrganizers.length === 0 ? (
              <div className="text-center py-12 text-white/50">
                <Layers className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No approved organizers</p>
              </div>
            ) : (
              <div className="space-y-2">
                {approvedOrganizers.map((organizer) => (
                  <div key={organizer.id} className="p-3 rounded-lg border border-white/10 bg-white/5 flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-white">{organizer.name}</span>
                        <span className="text-xs bg-white/10 px-2 py-0.5 rounded">{organizer.gameName}</span>
                        <span className="text-xs text-white/40">{organizer.achievementNames?.length || 0} achievements</span>
                      </div>
                      <p className="text-xs text-white/40 mt-1 truncate">{organizer.description || "No description"}</p>
                      <InlineTagEditor
                        tags={organizer.tags || []}
                        contentType="organizers"
                        itemId={organizer.id}
                        onUpdateTags={onUpdateTags}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onEdit?.(organizer, "organizers")}
                        className="border-white/20 text-white/60 hover:bg-white/5"
                        data-testid={`edit-approved-organizer-${organizer.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onDeleteOrganizer(organizer.id, organizer.name)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Planners Tab */}
          <TabsContent value="planners" className="flex-1 overflow-y-auto mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
              </div>
            ) : !approvedGamePlanners || approvedGamePlanners.length === 0 ? (
              <div className="text-center py-12 text-white/50">
                <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No approved planners</p>
              </div>
            ) : (
              <div className="space-y-2">
                {approvedGamePlanners.map((planner) => (
                  <div key={planner.id} className="p-3 rounded-lg border border-white/10 bg-white/5 flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-white">{planner.name}</span>
                        <span className="text-xs bg-white/10 px-2 py-0.5 rounded">{planner.gameName}</span>
                        <span className="text-xs text-white/40">
                          {planner.organizerData?.length || 0} organizer{(planner.organizerData?.length || 0) !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <p className="text-xs text-white/40 mt-1 truncate">{planner.description || "No description"}</p>
                      <InlineTagEditor
                        tags={planner.tags || []}
                        contentType="planners"
                        itemId={planner.id}
                        onUpdateTags={onUpdateTags}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onEdit?.(planner, "planners")}
                        className="border-white/20 text-white/60 hover:bg-white/5"
                        data-testid={`edit-approved-planner-${planner.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onDeletePlanner(planner.id, planner.name)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        data-testid={`delete-planner-${planner.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Grids Tab */}
          <TabsContent value="grids" className="flex-1 overflow-y-auto mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
              </div>
            ) : !approvedGrids || approvedGrids.length === 0 ? (
              <div className="text-center py-12 text-white/50">
                <Grid3X3 className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No approved grids</p>
              </div>
            ) : (
              <div className="space-y-2">
                {approvedGrids.map((grid) => (
                  <div key={grid.id} className="p-3 rounded-lg border border-white/10 bg-white/5 flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-white">{grid.name}</span>
                        {grid.gameId && (
                          <span className="text-xs bg-white/10 px-2 py-0.5 rounded">{grid.gameId}</span>
                        )}
                        <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">
                          {grid.rows?.length || 0}r x {grid.cols?.length || 0}c
                        </span>
                      </div>
                      <p className="text-xs text-white/40 mt-1 truncate">
                        {grid.description || "No description"} — by {grid.submittedByUsername || "Unknown"}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onEdit?.(grid, "grids")}
                        className="border-white/20 text-white/60 hover:bg-white/5"
                        data-testid={`edit-approved-grid-${grid.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onDeleteGrid(grid.id, grid.name)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        data-testid={`delete-approved-grid-${grid.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Checklists Tab */}
          <TabsContent value="checklists" className="flex-1 overflow-y-auto mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
              </div>
            ) : !approvedChecklists || approvedChecklists.length === 0 ? (
              <div className="text-center py-12 text-white/50">
                <ListTodo className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No shared checklists</p>
              </div>
            ) : (
              <div className="space-y-2">
                {approvedChecklists?.map((checklist) => (
                  <div key={checklist.id} className="p-3 rounded-lg border border-white/10 bg-white/5 flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-medium">{checklist.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          checklist.category === 'collectibles' ? 'bg-amber-500/20 text-amber-400' :
                          checklist.category === 'missables' ? 'bg-red-500/20 text-red-400' :
                          checklist.category === 'speedrun' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-purple-500/20 text-purple-400'
                        }`}>{checklist.category}</span>
                      </div>
                      <p className="text-xs text-white/50 mt-1">{checklist.gameName || "Unknown Game"}</p>
                      {checklist.description && (
                        <p className="text-sm text-white/60 mt-1 line-clamp-1">{checklist.description}</p>
                      )}
                      <p className="text-xs text-white/30 mt-1">
                        {checklist.itemCount || checklist.items?.length || 0} items • {checklist.copies || 0} copies
                        {checklist.authorName && ` • by ${checklist.authorName}`}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onDeleteChecklist(checklist.id, checklist.title)}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      data-testid={`delete-checklist-${checklist.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Maps Tab */}
          <TabsContent value="maps" className="flex-1 overflow-y-auto mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
              </div>
            ) : !approvedMaps || approvedMaps.length === 0 ? (
              <div className="text-center py-12 text-white/50">
                <MapPin className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No approved community maps</p>
              </div>
            ) : (
              <div className="space-y-2">
                {approvedMaps.map((map) => (
                  <div key={map.id} className="p-3 rounded-lg border border-white/10 bg-white/5 flex items-center justify-between gap-4" data-testid={`manage-map-${map.id}`}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {map.mapImageUrl && (
                        <div className="w-16 h-10 rounded overflow-hidden border border-white/10 flex-shrink-0">
                          <img src={map.mapImageUrl} alt={map.title} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-medium text-sm">{map.title}</span>
                          <span className="text-xs bg-white/10 px-2 py-0.5 rounded">{map.gameName}</span>
                        </div>
                        <p className="text-xs text-white/40 mt-0.5">
                          {(map.pins || []).length} pins • Adopted {map.adoptedCount || 0}x • Likes {map.likesCount || 0}
                          {map.submittedByUsername && ` • by ${map.submittedByUsername}`}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onDeleteMap(map.id, map.title)}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      data-testid={`delete-map-${map.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Trivia Tab */}
          <TabsContent value="trivia" className="flex-1 overflow-y-auto mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
              </div>
            ) : !approvedFacts || approvedFacts.length === 0 ? (
              <div className="text-center py-12 text-white/50">
                <Lightbulb className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No approved trivia</p>
              </div>
            ) : (
              <div className="space-y-2">
                {approvedFacts?.map((fact) => (
                  <div key={fact.id} className="p-3 rounded-lg border border-white/10 bg-white/5 flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs bg-white/10 px-2 py-0.5 rounded">{fact.gameName || fact.masterGameId}</span>
                        <span className="text-xs text-white/40">{fact.achievementName}</span>
                      </div>
                      <p className="text-sm text-white mt-1">{fact.text}</p>
                      {fact.submittedByUsername && (
                        <p className="text-xs text-white/30 mt-1">by {fact.submittedByUsername}</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onDeleteFact(fact.id)}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}


/**
 * PendingEditsDialog - Admin dialog to review pending edits on approved community items
 */
export function PendingEditsDialog({
  open,
  onOpenChange,
  pendingEdits,
  loading,
  onApproveEdit,
  onRejectEdit,
}) {
  const typeConfig = {
    challenges: { icon: Target, color: "text-orange-400", label: "Challenge" },
    organizers: { icon: Layers, color: "text-cyan-400", label: "Organizer" },
    planners: { icon: ClipboardList, color: "text-indigo-400", label: "Planner" },
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400">
            <Edit2 className="w-5 h-5" />
            Pending Edits Review ({pendingEdits?.length || 0})
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Review user-submitted edits to approved community items. The original version stays live until you approve.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
          </div>
        ) : !pendingEdits?.length ? (
          <div className="text-center py-8 text-white/40">
            No pending edits to review
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            {pendingEdits.map(item => {
              const config = typeConfig[item._itemType] || typeConfig.challenges;
              const Icon = config.icon;
              const edit = item.pendingEdit || {};
              
              return (
                <div key={item.id} className="bg-white/5 rounded-lg border border-white/10 p-4" data-testid={`pending-edit-${item.id}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className={`w-4 h-4 ${config.color}`} />
                    <span className="text-xs uppercase tracking-wider text-white/40">{config.label}</span>
                    <span className="text-xs text-white/30">|</span>
                    <span className="text-xs text-white/40">{item.gameName}</span>
                  </div>

                  {/* Current vs Proposed */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-white/40 mb-1 font-medium">Current (Live)</p>
                      <p className="text-white/80 font-medium">{item.name}</p>
                      {item.description && <p className="text-white/40 text-xs mt-1">{item.description}</p>}
                      {item.difficulty && <span className="text-xs text-white/40">Difficulty: {item.difficulty}</span>}
                      {item.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {item.tags.map(t => <span key={t} className="px-1.5 py-0.5 rounded-full bg-white/5 text-white/40 text-[10px] border border-white/10">{t}</span>)}
                        </div>
                      )}
                    </div>
                    <div className="border-l border-white/10 pl-3">
                      <p className="text-xs text-amber-400/80 mb-1 font-medium">Proposed Edit</p>
                      {edit.name && <p className={`font-medium ${edit.name !== item.name ? "text-amber-300" : "text-white/80"}`}>{edit.name}</p>}
                      {edit.description !== undefined && <p className={`text-xs mt-1 ${edit.description !== item.description ? "text-amber-300/80" : "text-white/40"}`}>{edit.description || "(cleared)"}</p>}
                      {edit.difficulty && <span className={`text-xs ${edit.difficulty !== item.difficulty ? "text-amber-300" : "text-white/40"}`}>Difficulty: {edit.difficulty}</span>}
                      {edit.tags && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {edit.tags.map(t => {
                            const isNew = !item.tags?.includes(t);
                            return <span key={t} className={`px-1.5 py-0.5 rounded-full text-[10px] border ${isNew ? "bg-green-500/15 text-green-300 border-green-500/30" : "bg-white/5 text-white/40 border-white/10"}`}>{t}</span>;
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Edit metadata & actions */}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
                    <span className="text-xs text-white/30">
                      Edited by {edit.editedByUsername} on {edit.editedAt ? new Date(edit.editedAt).toLocaleDateString() : "?"}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => onApproveEdit(item._itemType, item.id)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`approve-edit-${item.id}`}
                      >
                        <Check className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onRejectEdit(item._itemType, item.id)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        data-testid={`reject-edit-${item.id}`}
                      >
                        <X className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}



/**
 * InlineTagEditor - Small inline tag editor for admin content management
 */

function InlineTagEditor({ tags, contentType, itemId, onUpdateTags }) {
  const [editing, setEditing] = useState(false);
  const [localTags, setLocalTags] = useState(tags);
  const [newTag, setNewTag] = useState("");
  const [saving, setSaving] = useState(false);

  const handleAdd = () => {
    const tag = newTag.trim();
    if (tag && !localTags.includes(tag)) {
      setLocalTags(prev => [...prev, tag]);
    }
    setNewTag("");
  };

  const handleRemove = (tagToRemove) => {
    setLocalTags(prev => prev.filter(t => t !== tagToRemove));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdateTags?.(contentType, itemId, localTags);
      setEditing(false);
    } catch (err) {
      console.error("Failed to update tags:", err);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-1 mt-1 flex-wrap">
        {localTags.length > 0 ? (
          localTags.map(tag => (
            <span key={tag} className="px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-300/80 text-[10px] border border-purple-500/20">{tag}</span>
          ))
        ) : (
          <span className="text-[10px] text-white/20">No tags</span>
        )}
        <button
          onClick={() => { setLocalTags(tags); setEditing(true); }}
          className="text-[10px] text-purple-400 hover:text-purple-300 ml-1"
          data-testid={`edit-tags-${itemId}`}
        >
          <Edit2 className="w-3 h-3 inline" /> Edit
        </button>
      </div>
    );
  }

  return (
    <div className="mt-1 space-y-1.5">
      <div className="flex flex-wrap gap-1">
        {localTags.map(tag => (
          <span key={tag} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 text-[10px] border border-purple-500/30">
            {tag}
            <button onClick={() => handleRemove(tag)} className="hover:text-white">
              <X className="w-2.5 h-2.5" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-1">
        <Input
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
          placeholder="Add tag..."
          className="h-6 text-[11px] bg-black/50 border-white/10 px-2"
          data-testid={`tag-input-${itemId}`}
        />
        <Button size="sm" variant="outline" onClick={handleAdd} className="h-6 px-2 text-[10px] border-white/20">
          <Plus className="w-3 h-3" />
        </Button>
        <Button size="sm" onClick={handleSave} disabled={saving} className="h-6 px-2 text-[10px] bg-purple-600 hover:bg-purple-700">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
        </Button>
        <Button size="sm" variant="outline" onClick={() => { setLocalTags(tags); setEditing(false); }} className="h-6 px-2 text-[10px] border-white/20">
          <X className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}


/**
 * EditCommunityItemDialog - Edit dialog for community items (challenges, organizers, planners)
 */
export function EditCommunityItemDialog({
  open,
  onOpenChange,
  item,
  itemType, // "challenges" | "organizers" | "planners"
  onSave,
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [difficulty, setDifficulty] = useState("medium");
  const [itemTags, setItemTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);

  // Re-initialize when dialog opens or item changes
  useState(() => {
    if (item) {
      setName(item.name || "");
      setDescription(item.description || "");
      setDifficulty(item.difficulty || "medium");
      setItemTags(item.tags || []);
      setTagInput("");
    }
  }, [item]);

  // Also reset on open
  const handleOpen = (isOpen) => {
    if (isOpen && item) {
      setName(item.name || "");
      setDescription(item.description || "");
      setDifficulty(item.difficulty || "medium");
      setItemTags(item.tags || []);
      setTagInput("");
    }
    onOpenChange(isOpen);
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !itemTags.includes(tag)) {
      setItemTags(prev => [...prev, tag]);
    }
    setTagInput("");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = { name, description, tags: itemTags };
      if (itemType === "challenges") {
        data.difficulty = difficulty;
      }
      await onSave(itemType, item.id, data);
      onOpenChange(false);
    } catch (err) {
      console.error("Failed to save:", err);
    } finally {
      setSaving(false);
    }
  };

  const typeConfig = {
    challenges: { label: "Challenge", icon: Target, color: "text-orange-400", accent: "orange" },
    organizers: { label: "Organizer", icon: Layers, color: "text-cyan-400", accent: "cyan" },
    planners: { label: "Planner", icon: ClipboardList, color: "text-indigo-400", accent: "indigo" },
    grids: { label: "Grid", icon: Grid3X3, color: "text-purple-400", accent: "purple" },
  };
  const config = typeConfig[itemType] || typeConfig.challenges;
  const Icon = config.icon;

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${config.color}`}>
            <Icon className="w-5 h-5" />
            Edit {config.label}
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Edit the details of this community {config.label.toLowerCase()}
            {item?.status && <span className="ml-1 text-xs">({item.status})</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Name */}
          <div className="space-y-2">
            <Label className="text-white/70 text-sm">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-black/50 border-white/10"
              data-testid="edit-community-name"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="text-white/70 text-sm">Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-black/50 border-white/10 min-h-[80px] resize-none"
              placeholder="Add a description..."
              data-testid="edit-community-description"
            />
          </div>

          {/* Difficulty (challenges only) */}
          {itemType === "challenges" && (
            <div className="space-y-2">
              <Label className="text-white/70 text-sm">Difficulty</Label>
              <div className="flex gap-2">
                {["easy", "medium", "hard", "insane"].map(d => (
                  <button
                    key={d}
                    onClick={() => setDifficulty(d)}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                      difficulty === d
                        ? d === "easy" ? "bg-green-500/30 text-green-300 border border-green-500/50"
                        : d === "medium" ? "bg-yellow-500/30 text-yellow-300 border border-yellow-500/50"
                        : d === "hard" ? "bg-orange-500/30 text-orange-300 border border-orange-500/50"
                        : "bg-red-500/30 text-red-300 border border-red-500/50"
                        : "bg-white/5 text-white/50 border border-white/10 hover:border-white/20"
                    }`}
                    data-testid={`edit-difficulty-${d}`}
                  >
                    {d.charAt(0).toUpperCase() + d.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          <div className="space-y-2">
            <Label className="text-white/70 text-sm">Tags</Label>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); handleAddTag(); }
                }}
                placeholder="Add a tag and press Enter..."
                className="bg-black/50 border-white/10 text-sm flex-1"
                data-testid="edit-community-tag-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddTag}
                className="border-white/20 text-white/70 px-3"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {itemTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {itemTags.map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 text-xs border border-purple-500/30"
                  >
                    {tag}
                    <button
                      onClick={() => setItemTags(prev => prev.filter(t => t !== tag))}
                      className="hover:text-white transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          {item && (
            <div className="text-xs text-white/30 space-y-0.5">
              {item.gameName && <p>Game: {item.gameName}</p>}
              {item.submittedByUsername && <p>Submitted by: {item.submittedByUsername}</p>}
              {item.createdAt && <p>Date: {new Date(item.createdAt).toLocaleDateString()}</p>}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 border-white/20"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className={`flex-1 ${
              itemType === "challenges" ? "bg-orange-600 hover:bg-orange-700" :
              itemType === "organizers" ? "bg-cyan-600 hover:bg-cyan-700" :
              "bg-indigo-600 hover:bg-indigo-700"
            }`}
            data-testid="edit-community-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
